"use client";

import { DEPLOYMENT_ID } from "@/lib/version";

// Pin de deployment para los fetch() propios del cliente.
//
// Skew Protection de Vercel pinea al deployment de origen SOLO los requests
// que maneja el framework (assets, navegaciones RSC, server actions). Los
// fetch() de nuestro código — el polling de /api/casefiles, /api/competition,
// presencia, etc. — van SIEMPRE al deployment más nuevo. Con un deploy en el
// medio, un cliente viejo recibe respuestas del server nuevo: version skew.
// Incidente real (2026-07-02, Bariloche): tras un deploy, una sesión abierta
// quedó 70 minutos en un loop de "Maximum update depth exceeded" (React #185)
// hasta cerrar la pestaña.
//
// El fix es el que documenta Vercel para fetches custom: mandar el header
// `x-deployment-id` con el deployment que sirvió este bundle, y el edge rutea
// el request a ESE deployment. Parcheamos window.fetch una sola vez en vez de
// tocar cada call site (y los futuros).
//
// Alcance del pin — solo same-origin bajo /api/*, con dos exclusiones:
//   - /api/version: es el detector de "hay build nuevo" (use-app-update.ts).
//     Pineado le preguntaría al deployment viejo su propio buildId y jamás
//     detectaríamos un update.
//   - /ingest/* y cualquier otro path: afuera. Cross-origin ni se mira (un
//     header extra dispararía preflights CORS).
//
// Si el deployment pineado envejece más allá del max age de Skew Protection
// (12h) el edge devuelve 404: el polling falla en silencio (los pollers ya
// toleran !res.ok) y use-app-update hace rato que ofreció recargar.
// En local DEPLOYMENT_ID es "" y esto es un no-op total.

let installed = false;

function shouldPin(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    if (!url.pathname.startsWith("/api/")) return false;
    if (url.pathname === "/api/version") return false;
    return true;
  } catch {
    return false;
  }
}

function installDeploymentPin() {
  if (installed || typeof window === "undefined" || !DEPLOYMENT_ID) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const rawUrl =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      if (shouldPin(rawUrl)) {
        const headers = new Headers(
          init?.headers ??
            (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has("x-deployment-id")) {
          headers.set("x-deployment-id", DEPLOYMENT_ID);
          init = { ...init, headers };
        }
      }
    } catch {
      // Ante cualquier rareza el request sale sin pin — el pin nunca puede
      // ser motivo de que un fetch falle.
    }
    return originalFetch(input, init);
  };
}

// A nivel módulo (no en un useEffect): queda instalado apenas el bundle
// cliente evalúa este archivo, antes de que los providers disparen sus
// primeros fetches.
installDeploymentPin();

/** Componente vacío: existe para que el root layout importe (y evalúe) este
 *  módulo en el bundle cliente. Toda la magia pasa arriba, una sola vez. */
export function DeploymentPin() {
  return null;
}
